import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {
    Button,
    DatePicker,
    Drawer,
    Form,
    Input,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    Upload,
    message
} from 'antd';
import {
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Layout from './Layout';
import './HrStaffRegistryPage.css';

const {Text} = Typography;
const {Search} = Input;

const CHECK_COLUMNS = [
    {key: 'check_sb', title: 'СБ'},
    {key: 'check_summary', title: 'Сводные'},
    {key: 'check_eb', title: 'ЭБ'},
    {key: 'check_ppe', title: 'СИЗ'},
    {key: 'check_opp', title: 'ОПП'},
    {key: 'check_fire', title: 'Пож. Б'},
    {key: 'check_ot', title: 'ОТ'},
    {key: 'check_height_2', title: 'Выс. 2'},
    {key: 'check_height_3', title: 'Выс. 3'},
    {key: 'check_bm_electrical', title: 'БМ эл.'},
    {key: 'check_bm_height', title: 'БМ выс.'}
];

const DATE_FIELDS = [
    'birth_date',
    'event_date',
    'test_day_1',
    'test_day_2',
    'internship_start',
    'employment_date'
];

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const fmtDate = value => {
    if (!value) return '—';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD.MM.YYYY') : '—';
};

const toDayjs = value => {
    if (!value) return null;
    const d = dayjs(value);
    return d.isValid() ? d : null;
};

const BoolCell = ({value, onToggle, disabled}) => (
    <button
        type="button"
        className={`hr-staff-bool${value ? ' hr-staff-bool--yes' : ''}${
            value === false ? ' hr-staff-bool--no' : ''
        }`}
        disabled={disabled}
        onClick={() => onToggle(!value)}
        title={value ? 'Да — нажмите чтобы снять' : 'Нет — нажмите чтобы отметить'}
    >
        {value === true ? '✓' : value === false ? '—' : '·'}
    </button>
);

const HrStaffRegistryPage = () => {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({
        regions: ['МСК'],
        locations: [],
        formats: [],
        hr_managers: [],
        positions: []
    });
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [filters, setFilters] = useState({
        region: 'МСК',
        location: undefined,
        format: undefined,
        hr_manager: undefined,
        search: ''
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const loadMeta = useCallback(async () => {
        try {
            const {data} = await axios.get('/api/hr/staff-records/meta', {
                headers: authHeaders()
            });
            setMeta(data.data || {});
        } catch (_) {
            /* ignore */
        }
    }, []);

    const loadRows = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await axios.get('/api/hr/staff-records', {
                headers: authHeaders(),
                params: {
                    region: filters.region,
                    location: filters.location,
                    format: filters.format,
                    hr_manager: filters.hr_manager,
                    search: filters.search || undefined
                }
            });
            setRows(data.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось загрузить учёт персонала'
            );
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadMeta();
    }, [loadMeta]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const patchField = async (record, patch) => {
        try {
            await axios.put(
                `/api/hr/staff-records/${record.id}`,
                patch,
                {headers: authHeaders()}
            );
            setRows(prev =>
                prev.map(item =>
                    item.id === record.id ? {...item, ...patch} : item
                )
            );
        } catch (error) {
            message.error(
                error?.response?.data?.message || 'Не удалось сохранить'
            );
        }
    };

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({
            region_label: filters.region || 'МСК',
            is_active: true
        });
        setDrawerOpen(true);
    };

    const openEdit = record => {
        setEditing(record);
        const values = {...record};
        DATE_FIELDS.forEach(key => {
            values[key] = toDayjs(record[key]);
        });
        form.setFieldsValue(values);
        setDrawerOpen(true);
    };

    const saveDrawer = async () => {
        try {
            const values = await form.validateFields();
            const payload = {...values};
            DATE_FIELDS.forEach(key => {
                payload[key] = values[key]
                    ? values[key].format('YYYY-MM-DD')
                    : null;
            });

            if (editing?.id) {
                await axios.put(
                    `/api/hr/staff-records/${editing.id}`,
                    payload,
                    {headers: authHeaders()}
                );
                message.success('Сохранено');
            } else {
                await axios.post('/api/hr/staff-records', payload, {
                    headers: authHeaders()
                });
                message.success('Сотрудник добавлен');
            }
            setDrawerOpen(false);
            loadRows();
            loadMeta();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message || 'Не удалось сохранить'
            );
        }
    };

    const importDefaultCsv = async replace => {
        setImporting(true);
        try {
            const {data} = await axios.post(
                '/api/hr/staff-records/import',
                {region_label: filters.region || 'МСК', replace},
                {headers: authHeaders()}
            );
            const r = data.data || {};
            message.success(
                `Импорт: создано ${r.created || 0}, обновлено ${r.updated || 0}`
            );
            loadRows();
            loadMeta();
        } catch (error) {
            message.error(
                error?.response?.data?.message || 'Импорт не удался'
            );
        } finally {
            setImporting(false);
        }
    };

    const uploadCsv = async file => {
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('region_label', filters.region || 'МСК');
            const {data} = await axios.post(
                '/api/hr/staff-records/import',
                formData,
                {
                    headers: {
                        ...authHeaders(),
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            const r = data.data || {};
            message.success(
                `Импорт: создано ${r.created || 0}, обновлено ${r.updated || 0}`
            );
            loadRows();
            loadMeta();
        } catch (error) {
            message.error(
                error?.response?.data?.message || 'Импорт не удался'
            );
        } finally {
            setImporting(false);
        }
        return false;
    };

    const columns = useMemo(() => {
        const checkCols = CHECK_COLUMNS.map(col => ({
            title: col.title,
            dataIndex: col.key,
            width: 58,
            align: 'center',
            className: 'hr-staff-col-check',
            render: (value, record) => (
                <BoolCell
                    value={value}
                    onToggle={next =>
                        patchField(record, {[col.key]: next})
                    }
                />
            )
        }));

        return [
            {
                title: '№',
                dataIndex: 'sort_order',
                width: 48,
                fixed: 'left'
            },
            {
                title: 'ФИО',
                dataIndex: 'full_name',
                width: 220,
                fixed: 'left',
                render: (value, record) => (
                    <Space direction="vertical" size={0}>
                        <Text strong>{value}</Text>
                        {record.user ? (
                            <Text type="secondary" style={{fontSize: 11}}>
                                CRM: {record.user.last_name}{' '}
                                {record.user.first_name}
                            </Text>
                        ) : null}
                    </Space>
                )
            },
            {
                title: 'Локация',
                dataIndex: 'location',
                width: 130,
                render: v => v || '—'
            },
            {
                title: 'ЛА',
                dataIndex: 'personal_car',
                width: 56,
                render: v => v || '—'
            },
            {
                title: 'Телефон',
                dataIndex: 'phone',
                width: 140,
                render: v => v || '—'
            },
            {
                title: 'Должность',
                dataIndex: 'position_name',
                width: 180,
                ellipsis: true
            },
            {
                title: 'Даты',
                children: [
                    {
                        title: 'Рожд.',
                        dataIndex: 'birth_date',
                        width: 92,
                        render: fmtDate
                    },
                    {
                        title: 'Событие',
                        dataIndex: 'event_date',
                        width: 92,
                        render: fmtDate
                    },
                    {
                        title: '1 тест',
                        dataIndex: 'test_day_1',
                        width: 92,
                        render: fmtDate
                    },
                    {
                        title: '2 тест',
                        dataIndex: 'test_day_2',
                        width: 92,
                        render: fmtDate
                    },
                    {
                        title: 'Стаж.',
                        dataIndex: 'internship_start',
                        width: 92,
                        render: fmtDate
                    },
                    {
                        title: 'Труд-во',
                        dataIndex: 'employment_date',
                        width: 92,
                        render: fmtDate
                    }
                ]
            },
            {
                title: 'Договор',
                children: [
                    {
                        title: 'Формат',
                        dataIndex: 'contract_format',
                        width: 72,
                        render: v =>
                            v ? (
                                <Tag
                                    color={
                                        v.includes('ТК')
                                            ? 'blue'
                                            : 'gold'
                                    }
                                >
                                    {v}
                                </Tag>
                            ) : (
                                '—'
                            )
                    },
                    {
                        title: 'Номер',
                        dataIndex: 'contract_number',
                        width: 120,
                        ellipsis: true
                    },
                    {
                        title: 'Документы',
                        dataIndex: 'documents_path',
                        width: 160,
                        ellipsis: true,
                        render: v => v || '—'
                    }
                ]
            },
            {
                title: 'HR',
                dataIndex: 'hr_manager',
                width: 88,
                render: v => v || '—'
            },
            {
                title: 'Чек-лист',
                children: checkCols
            },
            {
                title: '',
                key: 'actions',
                width: 44,
                fixed: 'right',
                render: (_, record) => (
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(record)}
                    />
                )
            }
        ];
    }, []);

    return (
        <Layout>
            <div className="hr-staff-page">
                <div className="hr-staff-header">
                    <div>
                        <Text className="hr-staff-kicker">HR</Text>
                        <Typography.Title level={4} style={{margin: 0}}>
                            Учёт персонала
                        </Typography.Title>
                        <Text type="secondary">
                            Реестр сотрудников по локациям, договорам и
                            чек-листам документов
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadRows}
                            loading={loading}
                        >
                            Обновить
                        </Button>
                        <Button
                            loading={importing}
                            onClick={() => importDefaultCsv(false)}
                        >
                            Импорт МСК (CSV)
                        </Button>
                        <Upload
                            accept=".csv"
                            showUploadList={false}
                            beforeUpload={uploadCsv}
                        >
                            <Button
                                icon={<UploadOutlined />}
                                loading={importing}
                            >
                                Загрузить CSV
                            </Button>
                        </Upload>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                        >
                            Добавить
                        </Button>
                    </Space>
                </div>

                <div className="hr-staff-filters">
                    <Select
                        style={{width: 100}}
                        value={filters.region}
                        options={(meta.regions || ['МСК']).map(v => ({
                            value: v,
                            label: v
                        }))}
                        onChange={region =>
                            setFilters(prev => ({...prev, region}))
                        }
                    />
                    <Select
                        allowClear
                        placeholder="Локация"
                        style={{width: 160}}
                        value={filters.location}
                        options={(meta.locations || []).map(v => ({
                            value: v,
                            label: v
                        }))}
                        onChange={location =>
                            setFilters(prev => ({...prev, location}))
                        }
                    />
                    <Select
                        allowClear
                        placeholder="Формат"
                        style={{width: 120}}
                        value={filters.format}
                        options={(meta.formats || []).map(v => ({
                            value: v,
                            label: v
                        }))}
                        onChange={format =>
                            setFilters(prev => ({...prev, format}))
                        }
                    />
                    <Select
                        allowClear
                        placeholder="HR"
                        style={{width: 120}}
                        value={filters.hr_manager}
                        options={(meta.hr_managers || []).map(v => ({
                            value: v,
                            label: v
                        }))}
                        onChange={hr_manager =>
                            setFilters(prev => ({...prev, hr_manager}))
                        }
                    />
                    <Search
                        allowClear
                        placeholder="Поиск ФИО, телефон, договор…"
                        style={{width: 260}}
                        onSearch={search =>
                            setFilters(prev => ({...prev, search}))
                        }
                    />
                </div>

                <Table
                    className="hr-staff-table"
                    size="small"
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={rows}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: total => `Всего: ${total}`
                    }}
                    scroll={{x: 2400, y: 'calc(100vh - 280px)'}}
                />

                <Drawer
                    title={
                        editing ? 'Редактирование' : 'Новый сотрудник'
                    }
                    width={720}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    extra={
                        <Button type="primary" onClick={saveDrawer}>
                            Сохранить
                        </Button>
                    }
                >
                    <Form form={form} layout="vertical" size="small">
                        <div className="hr-staff-form-grid">
                            <Form.Item name="region_label" label="Регион">
                                <Input />
                            </Form.Item>
                            <Form.Item name="sort_order" label="№ п/п">
                                <Input type="number" />
                            </Form.Item>
                            <Form.Item
                                name="full_name"
                                label="ФИО"
                                rules={[{required: true}]}
                                className="hr-staff-form-span-2"
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item name="location" label="Локация">
                                <Input />
                            </Form.Item>
                            <Form.Item name="personal_car" label="ЛА">
                                <Input placeholder="Да / Нет / СДО" />
                            </Form.Item>
                            <Form.Item name="phone" label="Телефон">
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="position_name"
                                label="Должность"
                                className="hr-staff-form-span-2"
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item name="birth_date" label="Дата рождения">
                                <DatePicker style={{width: '100%'}} format="DD.MM.YYYY" />
                            </Form.Item>
                            <Form.Item name="event_date" label="Дата события">
                                <DatePicker style={{width: '100%'}} format="DD.MM.YYYY" />
                            </Form.Item>
                            <Form.Item name="test_day_1" label="1 тест. день">
                                <DatePicker style={{width: '100%'}} format="DD.MM.YYYY" />
                            </Form.Item>
                            <Form.Item name="test_day_2" label="2 тест. день">
                                <DatePicker style={{width: '100%'}} format="DD.MM.YYYY" />
                            </Form.Item>
                            <Form.Item name="internship_start" label="Нач. стажировки">
                                <DatePicker style={{width: '100%'}} format="DD.MM.YYYY" />
                            </Form.Item>
                            <Form.Item name="employment_date" label="Труд-во">
                                <DatePicker style={{width: '100%'}} format="DD.MM.YYYY" />
                            </Form.Item>
                            <Form.Item name="contract_format" label="Формат">
                                <Select
                                    allowClear
                                    options={[
                                        {value: 'ТК РФ', label: 'ТК РФ'},
                                        {value: 'ИП', label: 'ИП'}
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item name="contract_number" label="Номер договора">
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="documents_path"
                                label="Документы"
                                className="hr-staff-form-span-2"
                            >
                                <Input.TextArea rows={2} />
                            </Form.Item>
                            <Form.Item name="hr_manager" label="Ответственный HR">
                                <Input />
                            </Form.Item>
                        </div>
                        <Typography.Text strong>Чек-лист</Typography.Text>
                        <div className="hr-staff-check-grid">
                            {CHECK_COLUMNS.map(col => (
                                <Form.Item
                                    key={col.key}
                                    name={col.key}
                                    label={col.title}
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            ))}
                        </div>
                        <Form.Item name="notes" label="Примечание">
                            <Input.TextArea rows={2} />
                        </Form.Item>
                    </Form>
                </Drawer>
            </div>
        </Layout>
    );
};

export default HrStaffRegistryPage;
